import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { QuickBooks } from '../../../../../ModernScripts/Spotlight/Common/Images/Logos/DataSource/quickbooks';
import { COLOR, FONT_SIZE, SPACING } from '../../../../packages/constants';
import MyobLogoGenericRound from '../../../../packages/icons/myob-logo-generic-round';
import SageLogoGenericRound from '../../../../packages/icons/sage-logo-generic-round';
import XeroLogoGenericRound from '../../../../packages/icons/xero-logo-generic-round';
import { ButtonAsText } from '../../../button';
import { SearchFilter } from '../../../search-filter/search-filter';
import { DataSourceNames, TrackingSettingsTheme } from '../../tracking-settings-enums';
import TreeCollapseWrapper from '../TreeCollapseWrapper/tree-collapse-wrapper';

interface CategoryItem {
    id: string;
    name: string;
    isShown: boolean;
    budget?: string;
    trackingItemStatusCode?: string;
}

interface Category {
    id: string;
    name: string;
    items: CategoryItem[];
}

interface TrackedBudget {
    name: string;
    value: string;
}

export interface DataSource {
    id: string;
    dataSourceTypeCode: string;
    name: string;
    hasTracking?: boolean;
    orderPriority?: number;
    categories: Category[];
    budgets?: TrackedBudget[];
}

interface BuildCategoryData {
    dataSource: DataSource;
    categoryBuilder: (c: Category, i: number) => React.ReactElement;
}

export interface DataSignature {
    dataSources: DataSource[];
    hideArchivedTracking: boolean;
    displayUnselectedTrackingItems?: boolean;
}

export interface DataWrapperProps {
    handleUpdateData: (e: DataSignature) => void;
    data: DataSignature;
    dataIndex: number;
    dataItem: DataSource;
    dropdownData?: {
        name: string;
        value: string;
    }[];
    editAbbreviationFunction?: any;
    handleSaveOnChange?: (data: DataSignature, e?: HTMLInputElement) => void;
    organisationId?: string;
    showAbbreviations?: boolean;
    showExpandCollapseAll?: boolean;
    showSearchFilter?: boolean;
    theme?: TrackingSettingsTheme;
    treesExpanded?: boolean;
}

const StyledDataSourceWrapper = styled.div`
    margin-bottom: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? 0 : SPACING.XX_LARGE)};
`;

const ButtonWrapper = styled.div`
    display: flex;
    flex-shrink: 0;
    margin-right: ${SPACING.MEDIUM};
`;

const StyledDataWrapper = styled.div`
    border: 1px solid
        ${(props) =>
            props.theme === TrackingSettingsTheme.DataGrid ? COLOR.LEGACY.DATAGRID_BORDER : COLOR.MAIN.MEDIUM_GREY};
    border-radius: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '3px 3px 0 0' : 0)};
    background-color: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '#fff' : '#f7f9fb')};
    padding: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? COLOR.LEGACY.DATAGRID_BORDER : SPACING.MEDIUM};
    position: relative;
`;

const HeaderWrapper = styled.div`
    display: flex;
    align-items: center;
    padding: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? `8px ${SPACING.MEDIUM}` : `0 0 ${SPACING.MEDIUM}`};
    background-image: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? 'linear-gradient(-180deg,#f5f5f5,#f2f1f1)' : 'none'};
    box-shadow: ${(props) =>
        props.theme === TrackingSettingsTheme.DataGrid ? 'inset 0 1px 0 hsla(0,0%,100%,.5)' : 'none'};
    position: relative;
    gap: 8px;
`;

const Header = styled.h1`
    &&& {
        font-size: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '12px' : FONT_SIZE.LARGE)};
        line-height: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '16px' : FONT_SIZE.X_LARGE)};
        font-weight: 700;
        margin: 0 auto 0 ${SPACING.X_SMALL};
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }
`;

const StyledWrapper = styled.div`
    padding: ${SPACING.SMALL};
`;

const StyledParagraph = styled.p`
    &&& {
        font-size: ${FONT_SIZE.SMALL};
        padding: ${SPACING.SMALL};
        line-height: ${FONT_SIZE.LARGE};
        white-space: normal;
    }
`;

const StyledOverflow = styled.div`
    overflow: auto;
    max-height: 60vh;
`;

const StyledSearchFilter = styled(SearchFilter)`
    position: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? 'absolute' : 'static')};
    top: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '3px' : undefined)};
    right: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '3px' : undefined)};

    button {
        min-height: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '28px' : undefined)};
    }

    svg {
        height: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '11px' : undefined)};
        width: ${(props) => (props.theme === TrackingSettingsTheme.DataGrid ? '11px' : undefined)};
    }
`;

const LogoContainer = styled.div`
    flex-shrink: 0;
`;

const logoMap: { [key: string]: JSX.Element } = {
    [DataSourceNames.Xero]: <XeroLogoGenericRound width="18px" height="18px" />,
    [DataSourceNames.Quickbooks]: <QuickBooks width="98px" height="25px" />,
    [DataSourceNames.QuickBooksDesktop]: <QuickBooks width="98px" height="25px" />,
    [DataSourceNames.MyobAccountRight]: <MyobLogoGenericRound width="18px" height="18px" />,
    [DataSourceNames.MyobAccountRightDesktop]: <MyobLogoGenericRound width="18px" height="18px" />,
    [DataSourceNames.Sage50Desktop]: <SageLogoGenericRound width="18px" height="18px" />,
};

const BuildLogo = ({ name }: { name: string }) => {
    const logo = logoMap[name];
    return logo ? <LogoContainer>{logo}</LogoContainer> : null;
};

const BuildContent = ({ dataSource, categoryBuilder }: BuildCategoryData) => {
    if (!dataSource.hasTracking) {
        return (
            <StyledWrapper>
                <StyledParagraph>No tracking category data has been imported.</StyledParagraph>
                <StyledParagraph>
                    To import tracking category data, please navigate to the import screen and select the tracking
                    checkboxes before importing.
                </StyledParagraph>
            </StyledWrapper>
        );
    } else if (!dataSource.categories || dataSource.categories.length == 0) {
        return (
            <StyledWrapper>
                <StyledParagraph>
                    Tracking category data has not been enabled in your accounting system and therefore cannot be
                    imported into Spotlight.
                </StyledParagraph>
            </StyledWrapper>
        );
    } else {
        return (
            <StyledOverflow>
                {dataSource.categories
                    .sort((a: Category, b: Category) => (a.name > b.name ? 1 : -1))
                    .map(categoryBuilder)}
            </StyledOverflow>
        );
    }
};

const DataWrapper = ({
    data,
    dropdownData,
    dataItem,
    dataIndex,
    editAbbreviationFunction,
    handleUpdateData,
    handleSaveOnChange,
    organisationId,
    showAbbreviations,
    showExpandCollapseAll,
    showSearchFilter,
    theme,
    treesExpanded,
}: DataWrapperProps): JSX.Element => {
    const [isTreeExpanded, setTreeExpand] = useState(false);
    const filterInputRef = useRef<HTMLInputElement>(null);
    const [filterValue, setFilterValue] = useState('');

    const buildCategory = (category: Category, categoryIndex: number) => (
        <TreeCollapseWrapper
            data={data}
            dropdownData={dropdownData}
            dataIndex={dataIndex}
            isTreeExpanded={isTreeExpanded}
            dataItem={dataItem}
            category={category}
            categoryIndex={categoryIndex}
            key={`tree-${category.id}`}
            handleUpdateData={handleUpdateData}
            handleSaveOnChange={handleSaveOnChange}
            organisationId={organisationId}
            showAbbreviations={showAbbreviations}
            editAbbreviationFunction={editAbbreviationFunction}
            filterValue={filterValue}
            theme={theme}
        />
    );

    const handleFilterChange = (e: any) => {
        setFilterValue(e.target.value.toLowerCase());
    };

    const handleClearButton = () => {
        setFilterValue('');
        if (filterInputRef.current) {
            filterInputRef.current.focus();
        }
    };

    // TODO: improve this
    // This parent component sets the state to all of the tree components - to be collapsed or expanded.
    // If user collapses a tree component, this parent component still thinks it's open
    // This code below forces all to be expanded (true) then sets them to collapsed (false)
    const handleCollapseAll = () => {
        setTreeExpand(true);
        setTimeout(() => {
            setTreeExpand(false);
        }, 0);
    };

    // TODO: improve this
    const handleExpandAll = () => {
        setTreeExpand(false);
        setTimeout(() => {
            setTreeExpand(true);
        }, 0);
    };

    useEffect(() => {
        if (treesExpanded) setTreeExpand(treesExpanded);
    }, [treesExpanded]);

    return (
        <StyledDataSourceWrapper theme={theme} key={dataItem.id}>
            <StyledDataWrapper theme={theme}>
                <HeaderWrapper theme={theme}>
                    <BuildLogo name={dataItem.dataSourceTypeCode.toUpperCase()} />
                    <Header theme={theme} title={`${dataItem.dataSourceTypeCode.toUpperCase()}: ${dataItem.name}`}>
                        {dataItem.name}
                    </Header>
                    {showExpandCollapseAll && (
                        <ButtonWrapper>
                            <ButtonAsText
                                dataId={`organisation-tracking-settings__button-expand-${dataItem.dataSourceTypeCode.toLowerCase()}`}
                                onClick={handleExpandAll}
                            >
                                Expand all
                            </ButtonAsText>
                            <ButtonAsText
                                dataId={`organisation-tracking-settings__button-collapse-${dataItem.dataSourceTypeCode.toLowerCase()}`}
                                onClick={handleCollapseAll}
                            >
                                Collapse all
                            </ButtonAsText>
                        </ButtonWrapper>
                    )}
                    {showSearchFilter && (
                        <StyledSearchFilter
                            ref={filterInputRef}
                            handleChange={handleFilterChange}
                            searchTerm={filterValue}
                            assistAccess={false}
                            handleClearButton={handleClearButton}
                            placeholder={'Search...'}
                            theme={theme}
                            isRounded
                            isExpandable
                        />
                    )}
                </HeaderWrapper>
                <BuildContent dataSource={dataItem} categoryBuilder={buildCategory} />
            </StyledDataWrapper>
        </StyledDataSourceWrapper>
    );
};

export default DataWrapper;
